import { useState, useMemo, useEffect, useCallback } from "react";
import type { FeatureCollection } from "geojson";
import type { Job, Contact } from "../../../../../../models";
import { computeCommonValues, applyBulkEdits, hasMixedValues as checkMixedValues } from "./bulkEditHelpers";
import { useDropdownOptions } from "../../../../shared/useDropdownOptions";
import { findDeal } from "../../ImportTable.helpers";
import type { BulkEditModalProps, BulkEditModalViewModel, BulkEditColumnContext } from "./BulkEditModal.types";

/**
 * ViewModel hook for BulkEditModal
 *
 * This hook manages:
 * - Computing common values across selected jobs
 * - Form state for edited values
 * - Applying changes to all selected jobs
 * - Column context for mobile columns (bulk edit mode)
 */
export function useBulkEditModalViewModel(props: BulkEditModalProps): BulkEditModalViewModel {
	const { jobs, company, allDeals, onApply, onClose, user, integration } = props;

	// Compute common values when jobs change
	const commonValues = useMemo(() => computeCommonValues(jobs), [jobs]);

	// Form state - starts with common values
	const [formValues, setFormValues] = useState<Partial<Job>>(commonValues);
	const [isDirty, setIsDirty] = useState(false);

	// State for column context compatibility
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});

	// Per-job geometry edits: Record<jobId, Partial<Job['files']>>
	const [fileEdits, setFileEdits] = useState<Record<string, Partial<Job["files"]>>>({});

	// Jobs with per-job file edits overlaid
	const effectiveJobs = useMemo(
		() =>
			jobs.map((job) => {
				const edits = fileEdits[job.id];
				if (!edits) return job;
				return { ...job, files: { ...job.files, ...edits } };
			}),
		[jobs, fileEdits],
	);

	// Handle geometry edit from map (per-job)
	const onGeometryEdit = useCallback((jobId: string, key: string, fc: FeatureCollection) => {
		setFileEdits((prev) => ({
			...prev,
			[jobId]: { ...prev[jobId], [key]: fc },
		}));
		setIsDirty(true);
	}, []);

	// Get dropdown options based on the first job's deal (for consistency)
	const firstJob = jobs[0];
	const { gridSizes, depths, labPackages, micros } = useDropdownOptions({ allDeals, job: firstJob });

	// Reset form when jobs change (modal re-opens with different selection)
	useEffect(() => {
		const newCommonValues = computeCommonValues(jobs);
		setFormValues(newCommonValues);
		setFileEdits({});
		setIsDirty(false);
	}, [jobs]);

	// Set a specific field value
	const setFieldValue = useCallback((field: keyof Job, value: unknown) => {
		setFormValues((prev) => ({
			...prev,
			[field]: value,
		}));
		setIsDirty(true);
	}, []);

	// Reset form to common values
	const resetForm = useCallback(() => {
		setFormValues(commonValues);
		setFileEdits({});
		setIsDirty(false);
	}, [commonValues]);

	// Check if a field has mixed values
	const hasMixedValues = useCallback(
		(field: keyof Job): boolean => {
			return checkMixedValues(jobs, field);
		},
		[jobs],
	);

	// Handle applying changes to all jobs
	const handleApply = useCallback(() => {
		// Only apply fields that were actually edited (different from common values)
		const editedFields: Partial<Job> = {};

		for (const [key, value] of Object.entries(formValues)) {
			const commonValue = commonValues[key as keyof Job];
			// Include if value changed from common value, or if it was a mixed field that now has a value
			if (value !== commonValue || (hasMixedValues(key as keyof Job) && value !== undefined)) {
				(editedFields as Record<string, unknown>)[key] = value;
			}
		}

		const updatedJobs = applyBulkEdits(effectiveJobs, editedFields);
		onApply(updatedJobs);
		onClose();
	}, [formValues, commonValues, effectiveJobs, onApply, onClose, hasMixedValues]);

	// Handle closing with dirty check
	const handleClose = useCallback(() => {
		if (isDirty) {
			if (!window.confirm("You have unsaved changes. Are you sure you want to close?")) {
				return;
			}
		}
		onClose();
	}, [isDirty, onClose]);

	// Salesperson helpers
	const isNewSalesperson = (id: string): boolean => id.startsWith("temp-");
	const findNewSalesperson = (id: string): Contact | undefined => {
		return newSalespersons.find((sp) => sp.id === id);
	};

	// Create a placeholder job for column context (represents current form state)
	const placeholderJob = useMemo((): Job => {
		return {
			id: "bulk-edit-placeholder",
			...formValues,
		} as Job;
	}, [formValues]);

	// Column context for mobile columns
	const columnContext = useMemo((): BulkEditColumnContext => {
		const setJobs = (updater: Job[] | ((prev: Job[]) => Job[])) => {
			const currentJobs = [placeholderJob];
			const updated = typeof updater === "function" ? updater(currentJobs) : updater;
			const updatedJob = updated[0];

			if (updatedJob) {
				// Update form values from the updated job
				const newFormValues: Partial<Job> = {};
				for (const [key, value] of Object.entries(updatedJob)) {
					if (key !== "id") {
						(newFormValues as Record<string, unknown>)[key] = value;
					}
				}
				setFormValues(newFormValues);
				setIsDirty(true);
			}
		};

		// No-op validation for bulk edit mode
		const validateJobs = (): Record<string, string> => ({});

		// Find current deal from form values
		const currentDealId = formValues.dealIds?.[0];
		const currentDeal = allDeals.find((d) => d.id === currentDealId);

		return {
			props: {
				allDeals,
				selectedCompany: company,
				salespersons: props.salespersons,
				branches: props.branches,
				growers: props.growers,
				lastCropOptions: props.lastCropOptions,
				nextCropOptions: props.nextCropOptions,
			},
			jobs: [placeholderJob],
			setJobs,
			validateJobs,
			validationContext: {
				company,
				deal: currentDeal,
				allDeals,
				job: placeholderJob,
				user,
				hasPointsFile: false, // Not relevant for bulk edit
				isCreatingNewSalesperson: false,
				integration,
			},
			newSalespersons,
			setNewSalespersons,
			isNewSalesperson,
			findNewSalesperson,
			readySelectionState,
			setReadySelectionState,
			gridSizes,
			depths,
			labPackages,
			micros,
			findDeal,
		};
	}, [
		placeholderJob,
		formValues,
		allDeals,
		company,
		props.salespersons,
		props.branches,
		props.growers,
		props.lastCropOptions,
		props.nextCropOptions,
		user,
		integration,
		newSalespersons,
		readySelectionState,
		gridSizes,
		depths,
		labPackages,
		micros,
	]);

	return {
		commonValues,
		formValues,
		setFieldValue,
		resetForm,
		handleApply,
		handleClose,
		isDirty,
		columnContext,
		hasMixedValues,
		effectiveJobs,
		onGeometryEdit,
		isSuperUser: user?.isAdmin ?? false,
		dropdownOptions: {
			gridSizes,
			depths,
			labPackages,
			micros,
		},
	};
}
